export type StoryCategory = 'FOOD' | 'PLACE' | 'PEOPLE' | 'CHARACTER' | 'PLAY';

export interface NomoStory {
  id: string;
  category: StoryCategory;
  categoryZh: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  body: string[];
}

export const storyCategories: { key: StoryCategory; zh: string; label: string }[] = [
  { key: 'FOOD', zh: '食物故事', label: '一道菜为什么存在？' },
  { key: 'PLACE', zh: '地方故事', label: '这个地方有什么？' },
  { key: 'PEOPLE', zh: '人的故事', label: '当地人在想什么？' },
  { key: 'CHARACTER', zh: '角色故事', label: '我们为什么把它变成这个角色？' },
  { key: 'PLAY', zh: '潮玩故事', label: '它最终如何进入年轻人的生活？' },
];

export const nomoStories: NomoStory[] = [
  {
    id: 'west-lake-fish',
    category: 'FOOD',
    categoryZh: '食物故事',
    title: '一道西湖醋鱼，为什么让杭州人又爱又恨？',
    excerpt:
      '它是争议名菜，也是几代杭州人的味觉记忆。我们回到它最初的样子，去寻找那道酸里带甜的江南情绪。',
    image:
      'https://readdy.ai/api/search-image?query=Stylized%20elegant%20plate%20of%20Chinese%20West%20Lake%20vinegar%20fish%20with%20glossy%20amber%20sauce%20and%20ginger%20on%20a%20ceramic%20dish%20warm%20beige%20rice%20paper%20background%20minimal%20editorial%20food%20photography%20soft%20natural%20light%20high%20detail&width=900&height=620&seq=nomo-story-food-01&orientation=landscape',
    date: '2026.03',
    body: [
      '关于西湖醋鱼好不好吃，杭州人自己就能吵上三天三夜。有人嫌它太酸，有人说它太甜，还有人说它早就不该是「名菜」了。',
      '可奇怪的是，无论怎么争论，谁家要是宴请外地来的朋友，桌上总还是少不了一道西湖醋鱼。它像一个念旧的老朋友，你未必天天想它，却总在重要的日子里想起它。',
      '也许重要的从来不是那口味道本身，而是这道菜背后的念想——「鱼」与「余」同音，一年到头，有鱼有余，日子便不算亏待自己。',
      '鱼豆豆正是从这份念想里游出来的。它不替任何人评判这道菜，只想把「年年有余」的好彩头，轻轻放进你的口袋。',
    ],
  },
  {
    id: 'jiangnan-rain',
    category: 'PLACE',
    categoryZh: '地方故事',
    title: '江南的雨，是怎么长出一条鱼的',
    excerpt:
      '湖水、柳岸、巷口的屋檐与一场不停歇的梅雨，共同养出了鱼豆豆的柔软与松弛。',
    image:
      'https://readdy.ai/api/search-image?query=Serene%20southern%20Chinese%20jiangnan%20water%20town%20with%20misty%20willow%20trees%20stone%20bridge%20and%20calm%20lake%20in%20soft%20celadon%20green%20and%20warm%20beige%20tone%20minimal%20ink%20wash%20mood%20gentle%20morning%20light&width=900&height=620&seq=nomo-story-place-01&orientation=landscape',
    date: '2026.02',
    body: [
      '江南的雨，一下起来就没完没了。细密的雨丝落在湖面上，泛起一圈一圈的涟漪，像谁在水底轻轻地吐着泡泡。',
      '湖边的人早就习惯了这样的天气。他们打着伞，慢悠悠地走过石桥，走过柳岸，走过那些被雨洗得发亮的青石板路。',
      '这种不慌不忙、柔软松弛的气质，最终长成了鱼豆豆的样子——圆滚滚的，软乎乎的，带着一点西湖水汽的温柔。',
      '它相信，一座能容得下梅雨、也容得下心事的地方，才养得出真正的「好运」。',
    ],
  },
  {
    id: 'wish-good-life',
    category: 'PEOPLE',
    categoryZh: '人的故事',
    title: '这一代人，想要的好生活是什么样',
    excerpt:
      '不求大富大贵，只求吃得下、睡得着、心里松一点。我们在街头问了一百个人，答案出奇地相似。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20candid%20scene%20of%20young%20friends%20sharing%20a%20meal%20around%20a%20candle%20lit%20table%20at%20dusk%20soft%20amber%20and%20warm%20cream%20tones%20gentle%20blur%20cozy%20eastern%20lifestyle%20photography&width=900&height=620&seq=nomo-story-people-01&orientation=landscape',
    date: '2026.01',
    body: [
      '我们问了一百个人同一个问题：你想要的「好生活」，到底是什么样？',
      '答案出奇地相似——不是豪车，不是大房子，甚至不是存款多几个零。而是「吃得下、睡得着、心里松一点」。',
      '有人说是下班后能安心吃一顿热饭，有人说是周末能睡到自然醒，还有人说是和喜欢的人坐在一起，什么都不说也不尴尬。',
      '这些愿望听起来很小，却恰恰是最难被满足的。于是，我们想用一群软乎乎的 NOMO 灵，把这些「小确幸」重新放回你的生活里。',
    ],
  },
  {
    id: 'why-a-fish',
    category: 'CHARACTER',
    categoryZh: '角色故事',
    title: '我们为什么把一道菜，变成了一条小鱼',
    excerpt:
      '从味道到角色，中间隔着一次重新想象。鱼豆豆的每一处圆润，都是对「好运」最温柔的一次翻译。',
    image:
      'https://readdy.ai/api/search-image?query=Designer%20toy%20character%20sketch%20to%20final%20figure%20process%20on%20a%20clean%20warm%20beige%20desk%20with%20pencil%20and%20small%20clay%20sculpture%20soft%20studio%20lighting%20minimal%20creative%20workspace%20photography&width=900&height=620&seq=nomo-story-character-01&orientation=landscape',
    date: '2025.12',
    body: [
      '把一道菜变成一个角色，中间隔着一次小心翼翼的重新想象。我们不能只是给鱼戴上一顶帽子，就叫它「角色」。',
      '我们反复问自己：西湖醋鱼给我们的感受，到底是什么？答案是——酸甜，是松弛，是「年年有余」的安心。',
      '于是，鱼豆豆的每一处圆润，都是对这份感受的一次翻译。它没有锋利，只有柔软；它不催你成功，只祝你平安。',
      '从一味到一境，再到一愿，最后到一个能被捧在手心里的小生命。这就是 NOMOISM 做角色的全部秘密。',
    ],
  },
  {
    id: 'into-daily-life',
    category: 'PLAY',
    categoryZh: '潮玩故事',
    title: '一个潮玩，如何住进年轻人的书架',
    excerpt:
      '它不催你成功，也不要求你一直向前。它只是在你忙碌一天后，安静地陪你吃一顿饭。',
    image:
      'https://readdy.ai/api/search-image?query=Cozy%20modern%20shelf%20corner%20with%20small%20collectible%20art%20toys%20plants%20and%20warm%20lamplight%20soft%20beige%20and%20amber%20tones%20minimal%20lifestyle%20interior%20photography&width=900&height=620&seq=nomo-story-play-01&orientation=landscape',
    date: '2025.11',
    body: [
      '一个潮玩最好的归宿，不是被锁进玻璃柜里，而是被摆在书架上、书桌旁，成为生活里安静的一部分。',
      '鱼豆豆就是这样。它不会提醒你还有多少待办，也不会催促你继续往前跑。它只是坐在那里，陪你吃一顿饭，喝一杯茶。',
      '我们想做的，不是又一个被炒作的「限量」，而是一个愿意陪你很久的小生命。',
      '当你在忙碌一天后回到家，看到它软乎乎地待在那里，就会想起——生活里，总还有一点温柔在等你。',
    ],
  },
];